<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);
const isLocalExporting = ref(false);
const startDate = ref('');
const endDate = ref('');

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isExportingContact = computed(
  () => uiFlags.value.isExporting || isLocalExporting.value
);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);

// CSV 列(与联系人列表显示一致)。前端 API 响应深度 camelCase,但 filter 直连可能为 snake,故两种都兼容。
const CSV_HEADERS = [
  '姓名',
  '电话',
  '邮箱',
  '公司',
  '归属者',
  '微信',
  '名片状态',
  '保存时间',
  '来源URL',
  'IP',
];

const escapeCsv = val => {
  const s = (val ?? '').toString();
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const pick = (obj, camel, snake) =>
  obj ? obj[camel] ?? obj[snake] ?? '' : '';

const contactToRow = c => {
  const add = c.additionalAttributes || c.additional_attributes || {};
  const cus = c.customAttributes || c.custom_attributes || {};
  return [
    c.name,
    c.phoneNumber || c.phone_number || '',
    c.email || '',
    pick(add, 'companyName', 'company_name'),
    pick(cus, 'owner', 'owner'),
    pick(cus, 'wechat', 'wechat'),
    pick(cus, 'cardStatus', 'card_status'),
    pick(cus, 'contactSavedAt', 'contact_saved_at'),
    pick(add, 'referer', 'referer'),
    pick(add, 'createdAtIp', 'created_at_ip'),
  ]
    .map(escapeCsv)
    .join(',');
};

// 时间段过滤(按联系人创建时间 createdAt)。startDate/endDate 为 'YYYY-MM-DD'。
const inRange = c => {
  if (!startDate.value && !endDate.value) return true;
  const t = c.createdAt || c.created_at;
  if (!t) return false;
  const ts = new Date(t).getTime();
  if (
    startDate.value &&
    ts < new Date(`${startDate.value}T00:00:00`).getTime()
  )
    return false;
  if (endDate.value && ts > new Date(`${endDate.value}T23:59:59`).getTime())
    return false;
  return true;
};

const buildCsvBlob = rows => {
  const csv =
    '﻿' + CSV_HEADERS.map(escapeCsv).join(',') + '\n' + rows.join('\n');
  return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
};

// fallback: 不支持 showSaveFilePicker 的浏览器(Safari/Firefox),走默认下载
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const exportContacts = async () => {
  let query = { payload: [] };
  if (activeSegmentId.value && activeSegment.value) {
    query = activeSegment.value.query;
  } else if (Object.keys(appliedFilters.value).length > 0) {
    query = filterQueryGenerator(appliedFilters.value);
  }

  const filename = `contacts_${Date.now()}.csv`;
  isLocalExporting.value = true;

  // 先弹"另存为"让用户选保存路径。必须在用户点击激活有效期内,
  // 故放在耗时的分页拉取之前。File System Access API 仅 Chrome/Edge 支持;
  // 用户取消(AbortError)则中止;其它异常 fallback 到普通下载。
  let handle = null;
  if (typeof window.showSaveFilePicker === 'function') {
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'CSV 文件', accept: { 'text/csv': ['.csv'] } }],
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        isLocalExporting.value = false;
        return;
      }
      handle = null;
    }
  }

  try {
    const rows = [];
    let page = 1;
    // 分页拉取当前筛选条件下的全部联系人(每页 15),最多 200 页保护
    while (page <= 200) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await ContactAPI.filter(page, 'name', query);
      const { payload, meta } = data || {};
      (payload || []).forEach(c => {
        if (inRange(c)) rows.push(contactToRow(c));
      });
      if (!meta || !meta.hasMore) break;
      page += 1;
    }
    const blob = buildCsvBlob(rows);
    if (handle) {
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
    } else {
      saveBlob(blob, filename);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('导出联系人失败:', error);
  } finally {
    isLocalExporting.value = false;
  }
};

const handleDialogConfirm = async () => {
  await exportContacts();
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.TITLE')"
    :description="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DESCRIPTION')
    "
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.CONFIRM')
    "
    :is-loading="isExportingContact"
    :disable-confirm-button="isExportingContact"
    @confirm="handleDialogConfirm"
  >
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        时间段（按联系人创建时间，可选）
      </span>
      <div class="flex items-center gap-2">
        <input
          v-model="startDate"
          type="date"
          class="h-8 w-full rounded-lg border border-n-weak bg-transparent px-2 text-sm text-n-slate-12 outline-none dark:bg-n-solid-2"
        />
        <span class="text-sm text-n-slate-11">至</span>
        <input
          v-model="endDate"
          type="date"
          class="h-8 w-full rounded-lg border border-n-weak bg-transparent px-2 text-sm text-n-slate-12 outline-none dark:bg-n-solid-2"
        />
      </div>
      <p class="text-xs text-n-slate-11">留空则导出全部</p>
    </div>
  </Dialog>
</template>
